import { useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  EventRevisionState,
  type SourcingEventDto,
  useCreateEventChangeMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";
import { changesEditWholeEvent, openChangeDraft } from "./changeScope";
import { changeWindowOf, isOpenForNewChange } from "./changeWindow";
import { isNegotiationWithoutNotice } from "../../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";

export const useChangeFlow = (event: SourcingEventDto) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const editor = useDisclosure();
  const navigate = useNavigate();
  const [create, { isLoading: isStarting }] = useCreateEventChangeMutation();

  const isPrivate = event.eventContext.type === "Private";
  const changesRequest =
    isPrivate || (event.eventContext.type === "Public" && isNegotiationWithoutNotice(event.eventContext.procedureType));
  const changes = event.changes ?? [];
  const draft = openChangeDraft(event);
  const pending = [...changes].reverse().find((c) => c.state === EventRevisionState.PublicationPending);
  const published = changes.filter((c) => c.state === EventRevisionState.Published);
  const canStartChange =
    isOpenForNewChange(event) && !draft && !pending && changeWindowOf(event) !== "closed";

  const openChange = () => {
    if (changesEditWholeEvent(event)) navigate(urls.app.sourcingEvents.edit.go(event.id).info);
    else editor.onOpen();
  };

  const startChange = async () => {
    try {
      await create({ eventId: event.id }).unwrap();
      openChange();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const openChangeAtDeadline = () => {
    if (changesEditWholeEvent(event)) navigate(urls.app.sourcingEvents.edit.go(event.id).details);
    else editor.onOpen();
  };

  const startChangeAtDeadline = async () => {
    try {
      await create({ eventId: event.id }).unwrap();
      openChangeAtDeadline();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const actionLabel = draft
    ? t("Continue the change")
    : changesRequest
      ? t("Change the request")
      : t("Change the announcement");

  return { isPrivate, changesRequest, draft, pending, published, canStartChange, isStarting, editor, openChange, openChangeAtDeadline, startChange, startChangeAtDeadline, actionLabel };
};
